/**
 * Add-on module catalog — keys and labels mirrored from backend `modules.service.ts`.
 * Keep this file in sync with backend MODULE_DEFINITIONS.
 */

import { useAuthStore } from './auth.store';

export const MODULE_KEYS = [
  'FIELD_SERVICE',
  'ASSET_BLE',
  'CARBON',
  'EV',
  'VRP',
  'FUEL',
  'WEBHOOKS',
  'DASHCAM',
  'POD',
  'DVIR',
  'TIME_TRACKING',
] as const;

export type ModuleKey = (typeof MODULE_KEYS)[number];

export interface ModuleDefinitionClient {
  key: ModuleKey;
  label: string;
  shortLabel: string;
  priority: 1 | 2 | 3;
}

/** Lightweight client-side labels — backend holds the full catalog. */
export const MODULE_LABELS: Record<ModuleKey, ModuleDefinitionClient> = {
  FIELD_SERVICE: { key: 'FIELD_SERVICE', label: 'Missions Terrain', shortLabel: 'Missions', priority: 1 },
  ASSET_BLE: { key: 'ASSET_BLE', label: 'Asset Tracking BLE', shortLabel: 'Assets BLE', priority: 1 },
  CARBON: { key: 'CARBON', label: 'Empreinte Carbone & RSE', shortLabel: 'CO₂', priority: 1 },
  EV: { key: 'EV', label: 'Véhicules Électriques', shortLabel: 'EV', priority: 2 },
  VRP: { key: 'VRP', label: 'Optimisation de Tournées', shortLabel: 'Tournées', priority: 2 },
  FUEL: { key: 'FUEL', label: 'Gestion Carburant', shortLabel: 'Carburant', priority: 2 },
  WEBHOOKS: { key: 'WEBHOOKS', label: "Hub d'Intégrations", shortLabel: 'Intégrations', priority: 2 },
  DASHCAM: { key: 'DASHCAM', label: 'Vidéo / Dashcam IA', shortLabel: 'Dashcam', priority: 3 },
  POD: { key: 'POD', label: 'Preuve de Livraison', shortLabel: 'POD', priority: 3 },
  DVIR: { key: 'DVIR', label: 'Inspection Véhicule', shortLabel: 'DVIR', priority: 3 },
  TIME_TRACKING: { key: 'TIME_TRACKING', label: 'Temps de Travail', shortLabel: 'Pointage', priority: 3 },
};

/**
 * Check whether the currently authenticated user's tenant has a module enabled.
 * SUPER_ADMIN always returns true — they can see every UI for administration purposes.
 */
export function hasModule(moduleKey: ModuleKey): boolean {
  const user = useAuthStore.getState().user;
  if (!user) return false;
  if (user.role === 'SUPER_ADMIN') return true;
  return user.enabledModules?.includes(moduleKey) ?? false;
}

/**
 * React hook version — re-renders when the auth user changes.
 */
export function useHasModule(moduleKey: ModuleKey): boolean {
  const user = useAuthStore((state) => state.user);
  if (!user) return false;
  if (user.role === 'SUPER_ADMIN') return true;
  return user.enabledModules?.includes(moduleKey) ?? false;
}
